import { useEffect, useState } from "react";
import type { PublicSite } from "@shared/types";
import { get } from "./api";

const STORAGE_KEY = "lf.site";
const DEFAULTS: PublicSite = { alliance: true };

// Last known switches, so the nav does not flash a link on every load while
// the request is in flight. The Worker still refuses whatever is off.
function remembered(): PublicSite {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...DEFAULTS, ...(JSON.parse(raw) as Partial<PublicSite>) } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

let current = remembered();
let inflight: Promise<PublicSite> | null = null;
const listeners = new Set<(s: PublicSite) => void>();

/** Fetch once per page load; `refreshSite()` after an admin flips a switch. */
export function refreshSite(): Promise<PublicSite> {
  inflight = get<{ site: PublicSite }>("/site")
    .then((r) => {
      current = r.site;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
      } catch {
        // Private mode or blocked storage; the in-memory value still works.
      }
      listeners.forEach((fn) => fn(current));
      return current;
    })
    .catch(() => current);
  return inflight;
}

/** Public site switches — which optional pages are open. */
export function useSite(): PublicSite {
  const [site, setSite] = useState(current);

  useEffect(() => {
    listeners.add(setSite);
    if (!inflight) void refreshSite();
    return () => {
      listeners.delete(setSite);
    };
  }, []);

  return site;
}
